<template>
  <div class="order-line-item">
    <div class="order-line-item__info">
      <div class="order-line-item__name">{{ item.name }}</div>
      <div class="order-line-item__unit-price">{{ formattedUnitPrice }} each</div>
      <div v-if="stockWarning" class="order-line-item__stock-warning">
        Only {{ remainingStock }} left
      </div>
    </div>
    <div class="order-line-item__controls">
      <b-button
        size="sm"
        variant="outline-secondary"
        class="order-line-item__qty-btn"
        @click="decrement"
        :disabled="item.quantity <= 1"
        aria-label="Decrease quantity"
      >
        <i class="fas fa-minus"></i>
      </b-button>
      <span class="order-line-item__qty">{{ item.quantity }}</span>
      <b-button
        size="sm"
        variant="outline-secondary"
        class="order-line-item__qty-btn"
        @click="increment"
        aria-label="Increase quantity"
      >
        <i class="fas fa-plus"></i>
      </b-button>
    </div>
    <div class="order-line-item__total">{{ formattedLineTotal }}</div>
    <b-button
      variant="link"
      size="sm"
      class="order-line-item__remove"
      @click="remove"
      aria-label="Remove item"
    >
      <i class="fas fa-times"></i>
    </b-button>
  </div>
</template>

<script>
export default {
  name: "orderLineItem",
  props: {
    item: { type: Object, required: true },
  },
  computed: {
    lineTotal() {
      return parseFloat(this.item.price) * this.item.quantity;
    },
    formattedLineTotal() {
      return `R${this.lineTotal.toFixed(2)}`;
    },
    formattedUnitPrice() {
      return `R${parseFloat(this.item.price).toFixed(2)}`;
    },
    remainingStock() {
      return (this.item.stock_quantity || 0) - this.item.quantity;
    },
    stockWarning() {
      const remaining = this.remainingStock;
      return remaining >= 0 && remaining <= 2 && (this.item.stock_quantity || 0) > 0;
    },
  },
  methods: {
    increment() {
      this.$emit("update-quantity", {
        product_id: this.item.product_id,
        quantity: this.item.quantity + 1,
      });
    },
    decrement() {
      if (this.item.quantity <= 1) return;
      this.$emit("update-quantity", {
        product_id: this.item.product_id,
        quantity: this.item.quantity - 1,
      });
    },
    remove() {
      this.$emit("remove-item", this.item.product_id);
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/design/tokens/surfaces";
@import "~@/design/tokens/typography";
@import "~@/design/tokens/spacing";
@import "~@/design/tokens/feedback";

.order-line-item {
  display: flex;
  align-items: center;
  padding: $space-3 0;
  border-bottom: 1px solid $border-default;

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: $type-body;
    color: $text-primary;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__unit-price {
    font-size: $type-small;
    color: $text-muted;
  }

  &__stock-warning {
    font-size: $type-small;
    color: $feedback-warning;
    font-weight: 600;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: $space-2;
    margin: 0 $space-3;
  }

  &__qty-btn {
    min-width: 28px;
    min-height: 28px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $surface-elevated;
    border-color: $border-default;
    color: $text-primary;
  }

  &__qty {
    min-width: 24px;
    text-align: center;
    font-weight: 600;
    color: $text-primary;
  }

  &__total {
    font-weight: 600;
    color: $text-primary;
    min-width: 70px;
    text-align: right;
  }

  &__remove {
    color: $text-muted;
    padding: $space-1;
    margin-left: $space-2;

    &:hover {
      color: $feedback-error;
    }
  }
}
</style>
